import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { MapPin, MessageCircle, Calendar, Phone, Heart, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const Landing = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({
    responders_online: 0,
    total_responders: 0,
    veterans_supported: 0
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await axios.get(`${API}/public/stats`);
        setStats(response.data);
      } catch (error) {
        console.log("Could not load stats");
      }
    };
    loadStats();
  }, []);

  const handleLogin = () => {
    setIsLoading(true);
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const features = [
    {
      icon: MapPin,
      title: "Real-Time Location",
      description: "Track your responder's location in real-time as they come to support you."
    },
    {
      icon: MessageCircle,
      title: "Instant Chat",
      description: "Connect with your assigned responder through secure in-app messaging."
    },
    {
      icon: Calendar,
      title: "Follow-Up Care",
      description: "Schedule appointments for continued support and check-ins."
    },
    {
      icon: Phone,
      title: "Multiple Support Options",
      description: "Request in-person support or phone/video calls based on your needs."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top Blue Bar */}
      <div className="va-blue-bar h-2"></div>
      
      {/* Hero Section */}
      <header className="relative pattern-bg">
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between relative z-10">
          <div className="flex items-center gap-3">
            <img 
              src="/valor-logo2.png" 
              alt="V.A.L.O.R." 
              className="h-16 md:h-20 w-auto"
            />
            <div className="flex flex-col">
              <span className="text-2xl md:text-3xl font-extrabold text-primary" style={{ fontFamily: 'Manrope' }}>
                V.A.L.O.R.<span className="text-sm align-super">™</span>
              </span>
              <span className="text-xs text-muted-foreground -mt-1">Veterans' Alliance for Legislative &amp; Organizational Reform</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={handleLogin}
              disabled={isLoading}
              variant="outline"
              className="touch-target border-primary text-primary hover:bg-primary hover:text-white"
              data-testid="nav-signup-btn"
            >
              {isLoading ? "Loading..." : "Sign Up"}
            </Button>
            <Button 
              onClick={handleLogin}
              disabled={isLoading}
              className="touch-target bg-primary hover:bg-primary/90"
              data-testid="nav-login-btn"
            >
              {isLoading ? "Loading..." : "Sign In"}
            </Button>
          </div>
        </nav>

        <div className="container mx-auto px-6 py-16 lg:py-28 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="w-full bg-primary/10 text-primary px-6 py-3 rounded-lg">
                  <div className="flex items-center gap-3">
                    <img src="/valor-logo2.png" alt="" className="w-8 h-8" />
                    <span className="text-lg font-semibold tracking-wide">Veterans Crisis Support</span>
                    <span className="text-xs font-bold">™</span>
                  </div>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground leading-tight" style={{ fontFamily: 'Manrope' }}>
                  You're Not Alone.
                  <br />
                  <span className="text-secondary">Help is On The Way.</span>
                </h1>
                <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
                  Connect with trained peer responders who understand your journey. 
                  Get immediate in-person or phone support when you need it most.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleLogin}
                  disabled={isLoading}
                  className="crisis-button rounded-md bg-secondary hover:bg-secondary/90"
                  size="lg"
                  data-testid="hero-get-help-btn"
                >
                  <Heart className="w-5 h-5 mr-2" />
                  Get Veteran Peer Support
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="touch-target border-primary text-primary hover:bg-primary hover:text-white"
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="learn-more-btn"
                >
                  Learn More
                </Button>
              </div>

              {/* Quick Help Button - No Login Required */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-lg">
                <div className="flex items-center gap-3 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <span className="font-semibold text-red-800">Need Immediate Help?</span>
                </div>
                <p className="text-sm text-red-700 mb-3">
                  No account needed. Get a call from a peer responder now.
                </p>
                <Link to="/quick-help">
                  <Button 
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    data-testid="quick-help-btn"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Get Help Without Login
                  </Button>
                </Link>
              </div>

              {/* Scope Clarifier - Required Disclosure */}
              <div className="bg-muted/50 border border-border rounded-lg p-4 max-w-lg">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">VALORLink provides peer support from trained veteran volunteers.</strong>
                  {" "}This service is not a clinical, medical, or emergency service and is not a replacement for professional care.
                </p>
              </div>

              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 border-2 border-green-500">
                    <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                  </div>
                  <div>
                    <span className="text-lg font-bold text-green-600">{stats.responders_online}</span>
                    <span className="text-sm font-medium text-green-600 ml-1">Online</span>
                  </div>
                </div>
                <div className="h-6 w-px bg-border"></div>
                <div>
                  <span className="font-semibold text-foreground">{stats.veterans_supported}+</span>
                  <span className="text-sm text-muted-foreground ml-1">Veterans Supported</span>
                </div>
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="absolute -inset-4 bg-primary/5 rounded-2xl -rotate-3"></div>
              <img 
                src="https://images.pexels.com/photos/7468201/pexels-photo-7468201.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Veteran peer support"
                className="relative w-full h-[500px] object-cover rounded-2xl card-valor"
              />
              <div className="absolute bottom-6 left-6 right-6">
                <Card className="bg-white/95 backdrop-blur-sm card-valor border-0">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                      <img src="/valor-logo2.png" alt="" className="w-8 h-8" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>Peer Responders Ready</p>
                      <p className="text-sm text-muted-foreground">24/7 Support Available</p>
                    </div>
                    <div className="ml-auto">
                      <span className="status-dot status-dot-online"></span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section id="features" className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <p className="text-sm font-semibold text-secondary uppercase tracking-wide">How It Works</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Support at Your Fingertips
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Like a rideshare app, but for crisis support. Request help and a trained peer responder 
              will be on their way to assist you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="bg-white border-0 card-valor hover:shadow-lg transition-shadow"
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-6 space-y-4">
                  <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About V.A.L.O.R. Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center space-y-6">
              <div className="flex items-center justify-center gap-4">
                <img 
                  src="/valor-logo2.png" 
                  alt="V.A.L.O.R." 
                  className="h-20 md:h-24 w-auto"
                />
                <div className="text-left">
                  <span className="text-3xl md:text-4xl font-extrabold text-primary" style={{ fontFamily: 'Manrope' }}>
                    V.A.L.O.R.<span className="text-lg align-super">™</span>
                  </span>
                  <p className="text-sm text-muted-foreground">Veterans' Alliance for Legislative &amp; Organizational Reform</p>
                </div>
              </div>
              <p className="text-lg text-primary font-semibold">
                Empowering Veterans, Rebuilding Their Lives!
              </p>
            </div>
            
            <p className="text-lg text-muted-foreground leading-relaxed text-center">
              <strong>Veterans' Alliance for Legislative & Organizational Reform, Inc.</strong> (V.A.L.O.R.) 
              is a 501(c)(3) non-profit organization headquartered in Washington, D.C. We were formed to support 
              veterans in crisis through the development and implementation of VALORLink™ — a mobile application 
              aimed at supporting the Veterans Administration (VA) Crisis Hotline.
            </p>

            <p className="text-muted-foreground leading-relaxed text-center">
              Recognizing the alarming rates of veteran suicides and the critical need for accessible, timely assistance, 
              VALORLink™ serves as a comprehensive tool to augment existing hotline services. By harnessing the efficiency 
              of a platform similar to Uber, we provide a seamless and immediate connection between veterans in need and 
              compassionate fellow veteran volunteers, ensuring timely assistance and synchronization with the Veterans Crisis Hotline.
            </p>

            <div className="grid md:grid-cols-3 gap-6 pt-6">
              <div className="text-center p-6 bg-muted/30 rounded-lg">
                <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Manrope' }}>24/7</p>
                <p className="text-sm text-muted-foreground mt-1">Availability</p>
              </div>
              <div className="text-center p-6 bg-muted/30 rounded-lg">
                <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Manrope' }}>501(c)(3)</p>
                <p className="text-sm text-muted-foreground mt-1">Non-Profit Organization</p>
              </div>
              <div className="text-center p-6 bg-muted/30 rounded-lg">
                <p className="text-3xl font-bold text-primary" style={{ fontFamily: 'Manrope' }}>Free</p>
                <p className="text-sm text-muted-foreground mt-1">For All Veterans</p>
              </div>
            </div>

            {/* Core Values */}
            <div className="pt-8">
              <h3 className="text-xl font-bold text-foreground text-center mb-6" style={{ fontFamily: 'Manrope' }}>
                Our Core Values
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                {["Compassion", "Integrity", "Empowerment", "Collaboration", "Resilience", "Inclusivity"].map((value) => (
                  <span 
                    key={value}
                    className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium"
                  >
                    {value}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What VALORLink Is — and Isn't Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground text-center mb-10" style={{ fontFamily: 'Manrope' }}>
              What VALORLink Is — and Isn't
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* What VALORLink Is */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-green-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-green-700" style={{ fontFamily: 'Manrope' }}>
                    What VALORLink Is
                  </h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Veteran-to-veteran peer support</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Human connection and presence</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Help navigating next steps and resources</span>
                  </li>
                </ul>
              </div>

              {/* What VALORLink Is Not */}
              <div className="bg-white rounded-xl p-6 shadow-sm border border-red-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-red-700" style={{ fontFamily: 'Manrope' }}>
                    What VALORLink Is Not
                  </h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">A clinical or therapeutic service</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">Medical or psychiatric advice</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-muted-foreground">An emergency response system</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-white space-y-4 text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Manrope' }}>
                Ready to Connect?
              </h2>
              <p className="text-white/80 text-lg max-w-lg">
                Join our community of Veterans and peer responders. 
                Whether you need support or want to help others, we're here for you.
              </p>
              <Link to="/leaderboard" className="inline-block">
                <span className="text-white/60 hover:text-white underline text-sm">
                  View our Top Responders
                </span>
              </Link>
            </div>
            <Button 
              onClick={handleLogin}
              disabled={isLoading}
              size="lg"
              className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 font-bold whitespace-nowrap"
              data-testid="cta-join-btn"
            >
              Connect With a Veteran Peer
            </Button>
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-secondary text-white py-3 px-6 z-50">
        <div className="container mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center sm:text-left">
          <Phone className="w-5 h-5 animate-pulse-gentle" />
          <span className="font-medium">
            If you're in immediate danger, call <a href="tel:988" className="underline font-bold">988</a> (Press 1) or <a href="tel:911" className="underline font-bold">911</a>
          </span>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 border-t border-border mb-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col gap-6">
            {/* Logo and Copyright */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
                <span className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  VALORLink<span className="text-xs text-primary font-bold align-super">™</span>
                </span>
              </div>
              <p className="text-sm text-muted-foreground text-center sm:text-right">
                © 2025 V.A.L.O.R.™ Veterans' Alliance for Legislative & Organizational Reform, Inc. | Washington, D.C.
              </p>
            </div>
            
            {/* Safety Disclaimer */}
            <div className="border-t border-border pt-4">
              <p className="text-xs text-muted-foreground text-center leading-relaxed">
                <strong>Disclaimer:</strong> VALORLink provides peer support only and does not offer medical, mental health, or emergency services.
                If you are in immediate danger, please contact <a href="tel:911" className="text-primary hover:underline font-medium">911</a> or the 
                Veterans Crisis Hotline (<a href="tel:988" className="text-primary hover:underline font-medium">988</a>, Press 1).
              </p>
              <p className="text-xs text-muted-foreground text-center mt-2">
                <a href="/safety-and-ethics" className="text-primary hover:underline font-medium" data-testid="footer-safety-ethics-link">Safety & Ethics</a>
                <span className="mx-2 text-muted-foreground/50">|</span>
                <a href="/family-resources" className="text-primary hover:underline font-medium" data-testid="footer-family-resources-link">Family Resources</a>
                <span className="mx-2 text-muted-foreground/50">|</span>
                <a href="/contact" className="text-primary hover:underline font-medium" data-testid="footer-contact-link">Contact Us</a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
